import { z } from 'zod';
import React from 'react';
// Import directly from the source modules (not the '@/utils' barrel) to avoid a
// circular import: the barrel re-exports config.ts → @/types → back here, which
// left `zo` undefined when this module is reached first via the mcp-chat entry.
import { zo } from '@/utils/zod';
import type { __FieldContextHelpers } from '@/utils/field-context-helpers';
import { createThemeConfigSchema } from './theme';
import { ContextEngineSchemas, ContextEngine, CustomContext } from '../context';
import { ButtonProps } from '@/components/ui/Button';
import { TextProps } from '@/components/ui/Text';
import { AvatarProps } from '@/components/ui/Avatar';
import type { AvatarsContainerProps } from '@/components/ui/Avatars/components/AvatarsContainer';
import type { AvatarsItemProps } from '@/components/ui/Avatars/components/AvatarsItem';
import type { WidgetButtonRootProps } from '@/components/ui/WidgetButton/components/Root';
import type { WidgetButtonIconContainerProps } from '@/components/ui/WidgetButton/components/IconContainer';
import type { WidgetButtonOpenIconProps } from '@/components/ui/WidgetButton/components/OpenIcon';
import type { TypingIndicatorContainerProps } from '@/components/ui/TypingIndicator/components/Container';
import type { TypingIndicatorAvatarProps } from '@/components/ui/TypingIndicator/components/AvatarSlot';
import type { TypingIndicatorProps } from '@/components/ui/TypingIndicator';
import type { FloatingMessageContainerProps } from '@/components/ui/FloatingMessage/components/Container';
import type { OfflineMessageContainerProps } from '@/components/ui/OfflineMessage/components/Container';
import type { OfflineMessageEmailLinkProps } from '@/components/ui/OfflineMessage/components/EmailLink';
import type { OfflineMessageTitleTextProps } from '@/components/ui/OfflineMessage/components/TitleText';
import type { OfflineMessageSubtitleTextProps } from '@/components/ui/OfflineMessage/components/SubtitleText';
import type { OfflineMessageEmojiProps } from '@/components/ui/OfflineMessage/components/Emoji';
import type { ActionCardRootProps } from '@/components/ui/ActionCard/components/Root';
import type { ActionCardContentProps } from '@/components/ui/ActionCard/components/Content';
import type { ActionCardIconProps } from '@/components/ui/ActionCard/components/Icon';
import type { ActionCardTitleProps } from '@/components/ui/ActionCard/components/Title';
import type { ActionCardDescriptionProps } from '@/components/ui/ActionCard/components/Description';
import type { ActionCardProps } from '@/components/ui/ActionCard';
import type { HeaderContainerProps } from '@/components/ui/Header/components/Container';
import type { HeaderRootProps } from '@/components/ui/Header/components/Header';
import type { HeaderContentProps } from '@/components/ui/Header/components/Content';
import type { HeaderLogoProps } from '@/components/ui/Header/components/Logo';
import type { HeaderCloseButtonProps } from '@/components/ui/Header/components/CloseButton';
import type { HeaderTitlesProps } from '@/components/ui/Header/components/Titles';
import type { ToggleViewContainerProps } from '@/components/ui/ToggleView/components/Container';
import type { ToggleViewOptionProps } from '@/components/ui/ToggleView/components/Option';
import type { ToggleViewIconProps } from '@/components/ui/ToggleView/components/Icon';
import type { ToggleViewLabelProps } from '@/components/ui/ToggleView/components/Label';
import type { ChatMessageContainerProps } from '@/components/ui/ChatMessage/components/Container';
import type { ChatMessageContentProps } from '@/components/ui/ChatMessage/components/Content';
import type { ChatMessageMetaProps } from '@/components/ui/ChatMessage/components/Meta';
import type { ChatMessageTextProps } from '@/components/ui/ChatMessage/components/Message';
import type { ChatMessageProps } from '@/components/ui/ChatMessage';
import type { ChatInputProps } from '@/components/ui/ChatInput';
import type { PhoneInputProps } from '@/components/ui/PhoneInput';
import type { PhoneInputContainerProps } from '@/components/ui/PhoneInput/components/Container';
import type { PhoneInputCountrySelectProps } from '@/components/ui/PhoneInput/components/CountrySelect';
import type { PhoneInputNumberInputProps } from '@/components/ui/PhoneInput/components/NumberInput';
import type { FilterableCountryDropdownProps } from '@/components/ui/PhoneInput/components/FilterableCountryDropdown';
import type { ChatInputContainerProps } from '@/components/ui/ChatInput/components/Container';
import type { ChatInputTextareaProps } from '@/components/ui/ChatInput/components/Textarea';
import type { ChatInputSendButtonProps } from '@/components/ui/ChatInput/components/SendButton';
import type { ChatInputIconProps } from '@/components/ui/ChatInput/components/Icon';
import type { WidgetContainerProps } from '@/components/ui/Widget/components/Container';
import type { HomeContainerProps } from '@/components/Home/components/Container';
import type { ChatContainerProps } from '@/components/Chat/components/Container';
import type { QuestionnaireContainerProps } from '@/components/Questionnaire/components/Container';
import type { DateContainerProps } from '@/components/ui/FormElements/Date/components/Container';
import type { DateInputProps } from '@/components/ui/FormElements/Date/components/Input';
import type { DateProps } from '@/components/ui/FormElements/Date';
import type { DateUnsureCheckboxProps } from '@/components/ui/FormElements/Date/components/DateUnsureCheckbox';
import type { FormGroupContainerProps } from '@/components/ui/FormElements/FormGroup/components/Container';
import type { FormGroupLabelProps } from '@/components/ui/FormElements/FormGroup/components/Label';
import type { FormGroupErrorProps } from '@/components/ui/FormElements/FormGroup/components/Error';
import type { FormGroupProps } from '@/components/ui/FormElements/FormGroup';
import type { InputProps } from '@/components/ui/FormElements/Input';
import type { SelectProps } from '@/components/ui/FormElements/Select';
import type { TextareaProps } from '@/components/ui/FormElements/Textarea';
import { RadioProps } from '@/components/ui';
import { RadioContainerProps } from '@/components/ui/FormElements/Radio/components/Container';
import { RadioButtonProps } from '@/components/ui/FormElements/Radio/components/Button';
import { RadioInputProps } from '@/components/ui/FormElements/Radio/components/Input';
import { RadioItemProps } from '@/components/ui/FormElements/Radio/components/Item';
import { WidgetButtonProps } from '@/components/ui/WidgetButton';
import { FloatingMessageContentProps } from '@/components/ui/FloatingMessage/components/Content';
import { FloatingMessageTextProps } from '@/components/ui/FloatingMessage/components/Text';
import { QuestionnaireSubmitButtonProps } from '@/components/Questionnaire/components/SubmitButton';
import { QuestionnaireSubmitTextProps } from '@/components/Questionnaire/components/SubmitText';

export const StringOrContextFunctionSchema = zo.union([
  zo.string(),
  z.custom<
    (
      pageContext: ContextEngine.ContextResult | null,
      customContext: CustomContext | null
    ) => string
  >(),
]);

const FieldConfigSchema = z.object({
  name: z.string(),
  title: z.string(),
  label: z.string().optional(),
  type: z.enum([
    'text',
    'number',
    'email',
    'tel',
    'textarea',
    'select',
    'date',
    'radio',
    'range-slider',
    'hidden',
    'function',
  ]),
  enabled: z.boolean().optional(),
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  unsureCheckboxText: z.string().optional(),
  // Options can be string array (for radio, select) or number tuple array (for range-slider)
  options: z
    .union([z.array(z.string()), z.array(z.tuple([z.number(), z.number()]))])
    .optional(),
  rows: z.number().optional(),
  dependsOn: z
    .object({
      name: z.string(),
      value: z.string(),
    })
    .optional(),
  // Dynamic options can return strings or number tuples
  dynamicOptions: z
    .custom<
      (
        formValues: Record<string, string>,
        context: __FieldContextHelpers
      ) => string[] | Array<[number, number]> | undefined
    >()
    .optional(),
  // Independent min/max mode for range-slider (default: true)
  independentMinMax: z.boolean().optional(),
  // Generic measure for range-slider (e.g., currency symbol, units)
  // Can be a fixed string or a function that returns a string based on user's country code
  measure: z
    .union([z.string(), z.custom<(userCountryCode: string) => string>()])
    .optional(),
  validation: z
    .object({
      regex: z
        .object({
          pattern: z.string(),
          message: z.string(),
        })
        .optional(),
      validationType: z
        .enum([
          'text',
          'number',
          'email',
          'tel',
          'textarea',
          'select',
          'date',
          'radio',
          'range-slider',
          'hidden',
          'function',
        ])
        .optional(),
    })
    .optional(),
  // Default value for hidden fields (must be a string value, required for hidden fields)
  defaultValue: StringOrContextFunctionSchema.optional(),
  // Function for function type fields (must be a function, required for function fields)
  function: z.custom<
    (
      pageContext: ContextEngine.ContextResult | null,
      customContext: CustomContext | null
    ) => string
  >().optional(),
})
  .refine(
    (data) => {
      // Hidden fields must have a defaultValue (string or function)
      if (data.type === 'hidden') {
        if (!data.defaultValue) {
          return false;
        }
        // Allow string or function
        if (typeof data.defaultValue === 'string' && data.defaultValue.trim() === '') {
          return false;
        }
      }
      return true;
    },
    {
      message: 'Hidden fields must have a defaultValue (string or function)',
      path: ['defaultValue'],
    }
  )
  .refine(
    (data) => {
      // Function fields must have a function field (not defaultValue)
      if (data.type === 'function') {
        if (!data.function || typeof data.function !== 'function') {
          return false;
        }
      }
      return true;
    },
    {
      message: 'Function fields must have a function field',
      path: ['function'],
    }
  );

const createWidgetBaseConfigSchema = <T extends boolean>({
  optional: o,
}: {
  optional: T;
}) =>
  z.object({
    preset: z.string().optional(),  // Always optional, not required in internal config
    theme: createThemeConfigSchema({ optional: o }),
    context: ContextEngineSchemas.Config.optional(),
    customContext: z.record(z.string(), z.unknown()).optional(),
    contactEmail: zo.string().optional(o),
    behavior: zo
      .object({
        autoOpen: zo.boolean().optional(true),
        closeOnClickOutside: zo.boolean().optional(o),
        enableSound: zo.boolean().optional(o),
      })
      .optional(o),
    features: zo
      .object({
        enableHomeTab: zo.boolean().optional(o),
        home: zo
          .object({
            header: zo
              .object({
                title: zo.string().optional(o),
                subtitle: zo.string().optional(o),
              })
              .optional(o),
            chatActionButton: zo.object({
              title: zo.string().optional(o),
              description: zo.string().optional(o),
              titleWhenCompleted: zo.string().optional(o),
              descriptionWhenCompleted: zo.string().optional(o),
            }),
            linkButton: zo.object({
              title: zo.string().optional(o),
              description: zo.string().optional(o),
              url: zo.string(),
            }),
          })
          .optional(o),
        chat: zo
          .object({
            welcomeMessage: StringOrContextFunctionSchema.optional(o),
            confirmMessage: StringOrContextFunctionSchema.optional(o),
            errorMessage: StringOrContextFunctionSchema.optional(o),
            agentName: StringOrContextFunctionSchema.optional(o),
            skipQuestionnaire: zo.boolean().optional(o),
            submitButton: zo
              .object({
                text: zo.string().optional(o),
                loadingText: zo.string().optional(o),
              })
              .optional(o),
            fields: zo.array(FieldConfigSchema).optional(o),
          })
          .optional(o),
      })
      .optional(o),
    ui: zo
      .object({
        position: zo
          .enum(['bottom-right', 'bottom-left', 'top-right', 'top-left'])
          .optional(o),
        size: zo.enum(['compact', 'medium', 'large']).optional(o),
        hoveringMessage: zo.string().optional(o),
        floatingMessageDelay: zo.number().optional(o),
        floatingMessageAutoHideDelay: zo.number().optional(o),
        showFloatingMessage: zo.boolean().optional(o),
        showBubble: zo.boolean().optional(o),
        icon: zo.string().optional(o),
        target: zo
          .union([zo.string(), z.instanceof(HTMLElement), z.undefined()])
          .optional(o),
        formOnly: zo.boolean().optional(o),
      })
      .optional(o),
    gtm: zo
      .object({
        enabled: zo.boolean().optional(true),
        containerId: zo.string().optional(true),
        loadIfMissing: zo.boolean().optional(true),
      })
      .optional(true),  // Always optional - GTM is an opt-in feature
    customComponents: zo
      .object({
        // Text
        Text: z.custom<React.FC<TextProps>>().optional().nullable(),
        // Avatar
        Avatar: z.custom<React.FC<AvatarProps>>().optional().nullable(),
        // Avatars
        Avatars: z.custom<React.FC>().optional().nullable(),
        AvatarsContainer: z
          .custom<React.FC<AvatarsContainerProps>>()
          .optional()
          .nullable(),
        AvatarsItem: z
          .custom<React.FC<AvatarsItemProps>>()
          .optional()
          .nullable(),
        // WidgetButton
        WidgetButton: z
          .custom<React.FC<WidgetButtonProps>>()
          .optional()
          .nullable(),
        WidgetButtonRoot: z
          .custom<React.FC<WidgetButtonRootProps>>()
          .optional()
          .nullable(),
        WidgetButtonIconContainer: z
          .custom<React.FC<WidgetButtonIconContainerProps>>()
          .optional()
          .nullable(),
        WidgetButtonIcon: z
          .custom<React.FC<WidgetButtonOpenIconProps>>()
          .optional()
          .nullable(),
        WidgetButtonCloseIcon: z.custom<React.FC>().optional().nullable(),
        // TypingIndicator
        TypingIndicator: z
          .custom<React.FC<TypingIndicatorProps>>()
          .optional()
          .nullable(),
        TypingIndicatorContainer: z
          .custom<React.FC<TypingIndicatorContainerProps>>()
          .optional()
          .nullable(),
        TypingIndicatorAvatar: z
          .custom<React.FC<TypingIndicatorAvatarProps>>()
          .optional()
          .nullable(),
        TypingIndicatorDots: z.custom<React.FC>().optional().nullable(),
        // FloatingMessage
        FloatingMessage: z.custom<React.FC>().optional().nullable(),
        FloatingMessageContainer: z
          .custom<React.FC<FloatingMessageContainerProps>>()
          .optional()
          .nullable(),
        FloatingMessageContent: z
          .custom<React.FC<FloatingMessageContentProps>>()
          .optional()
          .nullable(),
        FloatingMessageText: z
          .custom<React.FC<FloatingMessageTextProps>>()
          .optional()
          .nullable(),
        FloatingMessageArrow: z.custom<React.FC>().optional().nullable(),
        // OfflineMessage
        OfflineMessage: z.custom<React.FC>().optional().nullable(),
        OfflineMessageContainer: z
          .custom<React.FC<OfflineMessageContainerProps>>()
          .optional()
          .nullable(),
        OfflineMessageEmoji: z
          .custom<React.FC<OfflineMessageEmojiProps>>()
          .optional()
          .nullable(),
        OfflineMessageTitleText: z
          .custom<React.FC<OfflineMessageTitleTextProps>>()
          .optional()
          .nullable(),
        OfflineMessageSubtitleText: z
          .custom<React.FC<OfflineMessageSubtitleTextProps>>()
          .optional()
          .nullable(),
        OfflineMessageEmailLink: z
          .custom<React.FC<OfflineMessageEmailLinkProps>>()
          .optional()
          .nullable(),
        // ActionCard
        ActionCard: z.custom<React.FC<ActionCardProps>>().optional().nullable(),
        ActionCardRoot: z
          .custom<React.FC<ActionCardRootProps>>()
          .optional()
          .nullable(),
        ActionCardContent: z
          .custom<React.FC<ActionCardContentProps>>()
          .optional()
          .nullable(),
        ActionCardTitle: z
          .custom<React.FC<ActionCardTitleProps>>()
          .optional()
          .nullable(),
        ActionCardDescription: z
          .custom<React.FC<ActionCardDescriptionProps>>()
          .optional()
          .nullable(),
        ActionCardIcon: z
          .custom<React.FC<ActionCardIconProps>>()
          .optional()
          .nullable(),
        // Header
        Header: z.custom<React.FC>().optional().nullable(),
        HeaderContainer: z
          .custom<React.FC<HeaderContainerProps>>()
          .optional()
          .nullable(),
        HeaderRoot: z.custom<React.FC<HeaderRootProps>>().optional().nullable(),
        HeaderContent: z
          .custom<React.FC<HeaderContentProps>>()
          .optional()
          .nullable(),
        HeaderLogo: z.custom<React.FC<HeaderLogoProps>>().optional().nullable(),
        HeaderCloseButton: z
          .custom<React.FC<HeaderCloseButtonProps>>()
          .optional()
          .nullable(),
        HeaderTitles: z
          .custom<React.FC<HeaderTitlesProps>>()
          .optional()
          .nullable(),
        // ToggleView
        ToggleView: z.custom<React.FC>().optional().nullable(),
        ToggleViewContainer: z
          .custom<React.FC<ToggleViewContainerProps>>()
          .optional()
          .nullable(),
        ToggleViewOption: z
          .custom<React.FC<ToggleViewOptionProps>>()
          .optional()
          .nullable(),
        ToggleViewIcon: z
          .custom<React.FC<ToggleViewIconProps>>()
          .optional()
          .nullable(),
        ToggleViewLabel: z
          .custom<React.FC<ToggleViewLabelProps>>()
          .optional()
          .nullable(),
        // ChatMessage
        ChatMessage: z
          .custom<React.FC<ChatMessageProps>>()
          .optional()
          .nullable(),
        ChatMessageContainer: z
          .custom<React.FC<ChatMessageContainerProps>>()
          .optional()
          .nullable(),
        ChatMessageContent: z
          .custom<React.FC<ChatMessageContentProps>>()
          .optional()
          .nullable(),
        ChatMessageMeta: z
          .custom<React.FC<ChatMessageMetaProps>>()
          .optional()
          .nullable(),
        ChatMessageText: z
          .custom<React.FC<ChatMessageTextProps>>()
          .optional()
          .nullable(),
        // ChatInput
        ChatInput: z.custom<React.FC<ChatInputProps>>().optional().nullable(),
        ChatInputContainer: z
          .custom<React.FC<ChatInputContainerProps>>()
          .optional()
          .nullable(),
        ChatInputTextarea: z
          .custom<React.FC<ChatInputTextareaProps>>()
          .optional()
          .nullable(),
        ChatInputSendButton: z
          .custom<React.FC<ChatInputSendButtonProps>>()
          .optional()
          .nullable(),
        ChatInputIcon: z
          .custom<React.FC<ChatInputIconProps>>()
          .optional()
          .nullable(),
        // Widget Container
        WidgetContainer: z
          .custom<React.FC<WidgetContainerProps>>()
          .optional()
          .nullable(),
        // Home Container
        HomeContainer: z
          .custom<React.FC<HomeContainerProps>>()
          .optional()
          .nullable(),
        // Chat Container
        ChatContainer: z
          .custom<React.FC<ChatContainerProps>>()
          .optional()
          .nullable(),
        // Questionnaire Container
        QuestionnaireContainer: z
          .custom<React.FC<QuestionnaireContainerProps>>()
          .optional()
          .nullable(),
        QuestionnaireSubmitButton: z
          .custom<React.FC<QuestionnaireSubmitButtonProps>>()
          .optional()
          .nullable(),
        QuestionnaireSubmitLoading: z.custom<React.FC>().optional().nullable(),
        QuestionnaireSubmitIcon: z
          .custom<React.FC<{ color: string }>>()
          .optional()
          .nullable(),
        QuestionnaireSubmitText: z
          .custom<React.FC<QuestionnaireSubmitTextProps>>()
          .optional()
          .nullable(),
        // Date
        Date: z.custom<React.FC<DateProps>>().optional().nullable(),
        DateContainer: z
          .custom<React.FC<DateContainerProps>>()
          .optional()
          .nullable(),
        DateInput: z.custom<React.FC<DateInputProps>>().optional().nullable(),
        DateUnsureCheckbox: z
          .custom<React.FC<DateUnsureCheckboxProps>>()
          .optional()
          .nullable(),
        // FormGroup
        FormGroup: z.custom<React.FC<FormGroupProps>>().optional().nullable(),
        FormGroupContainer: z
          .custom<React.FC<FormGroupContainerProps>>()
          .optional()
          .nullable(),
        FormGroupLabel: z
          .custom<React.FC<FormGroupLabelProps>>()
          .optional()
          .nullable(),
        FormGroupError: z
          .custom<React.FC<FormGroupErrorProps>>()
          .optional()
          .nullable(),
        // Input
        Input: z.custom<React.FC<InputProps>>().optional().nullable(),
        // Select
        Select: z.custom<React.FC<SelectProps>>().optional().nullable(),
        // Textarea
        Textarea: z
          .custom<
            React.ForwardRefExoticComponent<
              TextareaProps & React.RefAttributes<HTMLTextAreaElement>
            >
          >()
          .optional()
          .nullable(),
        // PhoneInput
        PhoneInput: z.custom<React.FC<PhoneInputProps>>().optional().nullable(),
        PhoneInputContainer: z
          .custom<React.FC<PhoneInputContainerProps>>()
          .optional()
          .nullable(),
        PhoneInputCountrySelect: z
          .custom<React.FC<PhoneInputCountrySelectProps>>()
          .optional()
          .nullable(),
        PhoneInputCountryDropdown: z
          .custom<React.FC<FilterableCountryDropdownProps>>()
          .optional()
          .nullable(),
        PhoneInputNumberInput: z
          .custom<React.FC<PhoneInputNumberInputProps>>()
          .optional()
          .nullable(),
        // Button
        Button: z.custom<React.FC<ButtonProps>>().optional().nullable(),
        // Radio
        Radio: z.custom<React.FC<RadioProps>>().optional().nullable(),
        RadioContainer: z
          .custom<React.FC<RadioContainerProps>>()
          .optional()
          .nullable(),
        RadioItem: z.custom<React.FC<RadioItemProps>>().optional().nullable(),
        RadioButton: z
          .custom<React.FC<RadioButtonProps>>()
          .optional()
          .nullable(),
        RadioInput: z.custom<React.FC<RadioInputProps>>().optional().nullable(),
      })
      .optional(o),
  });

export { FieldConfigSchema, createWidgetBaseConfigSchema };
